import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const URL_RE = /((?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)"'\]])/gi;
const TAG_RE = /(^|[^\w])#([\wÀ-ɏ]{1,50})/g;

/** Turns #hashtags into links to /tag/:tag */
export function hashtagify(text, { dark = false } = {}) {
  const out = [];
  let last = 0, m, k = 0;
  const s = String(text);
  while ((m = TAG_RE.exec(s)) !== null) {
    const start = m.index + m[1].length;
    if (start > last) out.push(s.slice(last, start));
    const tag = m[2];
    out.push(
      <Link key={`t${k++}`} to={`/tag/${encodeURIComponent(tag.toLowerCase())}`} onClick={(e) => e.stopPropagation()} data-testid="post-hashtag"
        className={`font-semibold hover:underline ${dark ? "text-sky-300" : "text-primary"}`}>#{tag}</Link>
    );
    last = start + tag.length + 1;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

const MENTION_RE = /(^|[^\w@])@([A-Za-zÀ-ɏ0-9._-]{2,30})/g;

/** Turns @Nome into a profile link. Uses the structured `mentions` list ([{user_id,name}]) when available,
 *  otherwise falls back to a people search. */
export function mentionify(text, { dark = false, mentions = [] } = {}) {
  const out = [];
  let last = 0, m, k = 0;
  const s = String(text);
  const list = Array.isArray(mentions) ? mentions : [];
  while ((m = MENTION_RE.exec(s)) !== null) {
    const start = m.index + m[1].length;
    if (start > last) out.push(s.slice(last, start));
    const tok = m[2].replace(/[._-]+$/, "");
    const lower = tok.toLowerCase();
    const hit = list.find((u) => (u.name || "").toLowerCase().split(/\s+/)[0] === lower)
      || list.find((u) => (u.name || "").toLowerCase().replace(/\s+/g, "") === lower)
      || list.find((u) => (u.name || "").toLowerCase().startsWith(lower));
    const to = hit ? `/user/${hit.user_id}` : `/search?q=${encodeURIComponent(tok)}&tab=people`;
    out.push(
      <Link key={`m${k++}`} to={to} onClick={(e) => e.stopPropagation()} data-testid="post-mention" title={hit ? hit.name : `Buscar ${tok}`}
        className={`font-semibold hover:underline ${dark ? "text-sky-200" : "text-primary"}`}>@{tok}</Link>
    );
    last = start + 1 + tok.length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

function decorate(part, opts) {
  return hashtagify(part, opts).flatMap((n, j) =>
    typeof n === "string"
      ? mentionify(n, opts).map((x, q) => (typeof x === "string" ? x : React.cloneElement(x, { key: `${j}-${q}` })))
      : [React.cloneElement(n, { key: `h${j}` })]
  );
}

export function linkify(text, opts = {}) {
  const parts = String(text).split(URL_RE);
  const linkClass = opts.dark
    ? "text-white underline underline-offset-2 break-all hover:opacity-80 font-medium"
    : "text-primary underline underline-offset-2 break-all hover:opacity-80";
  return parts.flatMap((part, i) => {
    if (i % 2 === 1) {
      const href = part.startsWith("http") ? part : `https://${part}`;
      return (
        <a key={i} href={href} target="_blank" rel="noopener noreferrer nofollow" data-testid="post-autolink"
          onClick={(e) => e.stopPropagation()} className={linkClass}>{part}</a>
      );
    }
    return decorate(part, opts).map((n, j) => (typeof n === "string" ? n : React.cloneElement(n, { key: `${i}-${j}` })));
  });
}

export default function ExpandableText({ text, lines = 3, className = "", testid = "post-content", mentions }) {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setClamped(el.scrollHeight > el.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, lines]);

  if (!text) return null;
  const clampClass = expanded ? "" : (lines === 3 ? "line-clamp-3" : `line-clamp-${lines}`);
  return (
    <div className={className}>
      <p ref={ref} data-testid={testid} className={`whitespace-pre-wrap break-words ${clampClass}`}>{linkify(text, { mentions })}</p>
      {(clamped || expanded) && (
        <button
          type="button"
          data-testid={`${testid}-toggle`}
          onClick={() => setExpanded(e => !e)}
          className="mt-1 text-sm font-semibold text-primary hover:underline"
        >
          {expanded ? "Ler menos" : "Ler mais"}
        </button>
      )}
    </div>
  );
}
